import { get, authPost, authPut, authPatch, authDelete } from './apiService';

const BASE = '/api/sprint-health';
const fresh = { useCache: false };
const enc = encodeURIComponent;

export const getSprintHealthConfig = () => get(`${BASE}/config`, null, 'json', fresh);
export const getSprintHealthTeams = () => get(`${BASE}/teams`);
export const getSprintHealthSprints = () => get(`${BASE}/sprints`);

export const getTeamHealth = (sprint, team, refresh = false) =>
  get(`${BASE}/team/${enc(sprint)}/${enc(team)}`, refresh ? { refresh: 1 } : null, 'json', fresh);
export const getLeadershipHealth = (sprint) => get(`${BASE}/leadership/${enc(sprint)}`, null, 'json', fresh);

export const getCommitmentRows = (year, quarter, team) =>
  get(`${BASE}/commitments/${enc(year)}/${enc(quarter)}`, team ? { team } : null, 'json', fresh);
/** The owning team is read from the item's Team field in ADO. */
export const addCommitment = (year, quarter, adoId) =>
  authPost(`${BASE}/commitments/${enc(year)}/${enc(quarter)}`, { adoId });
export const refreshCommitments = (year, quarter, team) =>
  authPost(`${BASE}/commitments/${enc(year)}/${enc(quarter)}/refresh`, team ? { team } : {});
export const removeCommitment = (year, quarter, team, adoId) =>
  authDelete(`${BASE}/commitments/${enc(year)}/${enc(quarter)}/${enc(team)}/${enc(adoId)}`);

/** Re-imports the sprint's work items from ADO, rebuilds the team's sprint data, syncs goal and commitments, recomputes. */
export const refreshTeamSprint = (sprint, team) => authPost(`${BASE}/refresh/${enc(sprint)}/${enc(team)}`, {});
export const syncGoals = (sprint, team) => authPost(`${BASE}/goals/sync/${enc(sprint)}`, team ? { team } : {});
/** { reviewHeld?, retroHeld? }: true, false or null (not recorded). */
export const setCeremonies = (sprint, team, body) => authPatch(`${BASE}/checkin/${enc(sprint)}/${enc(team)}/ceremonies`, body);
/** Replaces the sprint's retro actions: [{ text, done }], carried ones included. */
export const saveRetroActions = (sprint, team, actions) => authPut(`${BASE}/checkin/${enc(sprint)}/${enc(team)}/retro-actions`, { actions });

export const generateNotes = (sprint, team) => authPost(`${BASE}/notes/generate/${enc(sprint)}`, team ? { team } : {});
/** `html` is the formatted version (bold, italic, lists); `text` is the plain fallback. */
export const addTeamNote = (sprint, team, text, html) => authPost(`${BASE}/notes/${enc(sprint)}/${enc(team)}`, html ? { html } : { text });
export const updateNote = (sprint, team, idx, body) => authPatch(`${BASE}/notes/${enc(sprint)}/${enc(team)}/${idx}`, body);
export const deleteNote = (sprint, team, idx) => authDelete(`${BASE}/notes/${enc(sprint)}/${enc(team)}/${idx}`);
/** Team-set goal outcome: 'met' | 'partly' | 'not_met' | null (null = use the ADO toggle). */
export const setGoalOutcome = (sprint, team, outcome) => authPut(`${BASE}/goal-outcome/${enc(sprint)}/${enc(team)}`, { outcome });

/** Email HTML for copy-and-paste. team = null gives the leadership (all teams) version. */
export const getSprintEmail = (sprint, team) => get(`${BASE}/email/${enc(sprint)}`, team ? { team } : null, 'json', fresh);

export const OUTCOME = {
  met: { label: 'Goal met', tone: 'ok' },
  partly: { label: 'Goal partly met', tone: 'warn' },
  not_met: { label: 'Goal not met', tone: 'bad' },
  not_recorded: { label: 'Outcome not recorded', tone: 'none' },
  none: { label: 'No goal set', tone: 'none' },
  unknown: { label: 'Goal not synced from ADO', tone: 'none' },
};

export const STATUS_TONE = { healthy: 'ok', watch: 'warn', conversation: 'bad' };

export const shortSprint = (name) => (String(name || '').match(/_(S\d+)_/) || [])[1] || String(name || '');

/**
 * Split a sprint goal for display. "HighQ Next - A, B and C" → { area: 'HighQ Next', items: ['A', 'B', 'C'] }.
 * Splits on new lines, semicolons and top-level commas (never inside brackets), dropping a leading "and".
 * Short or single-clause goals come back as one item. Keep in sync with server/services/sprintHealthCalc.js.
 */
export function formatGoal(text) {
  const raw = String(text || '').trim();
  if (!raw) return { area: null, items: [] };
  let area = null;
  let rest = raw;
  const m = raw.match(/^([^-–:\n]{2,40}?)\s+[-–:]\s+([\s\S]+)$/);
  if (m) { area = m[1].trim(); rest = m[2].trim(); }
  const parts = [];
  let depth = 0;
  let cur = '';
  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (ch === '(' || ch === '[') depth++;
    if ((ch === ')' || ch === ']') && depth > 0) depth--;
    if (depth === 0 && (ch === '\n' || ch === ';' || ch === ',')) { parts.push(cur); cur = ''; continue; }
    cur += ch;
  }
  parts.push(cur);
  let items = parts.map((p) => p.trim()).filter(Boolean);
  // "…, and kicking off X" / "A and B" in the last clause
  items = items.flatMap((p, i) => (i === items.length - 1 && items.length > 1 ? p.split(/^and\s+/i).filter(Boolean) : [p]))
    .map((p) => p.replace(/^and\s+/i, '').replace(/[.]+$/, '').trim())
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1));
  if (items.length < 2 || raw.length < 90) return { area, items: [m ? rest : raw] };
  return { area, items };
}
